import {
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { ChangeEvent, useCallback } from 'react';
import { differenceWith, unique } from 'remeda';
import { NotificationChannelType } from 'src/graphql/entities/notification/notification.types';
import {
  NotificationEventResponse,
  NotificationPreferenceInputData,
  NotificationRoleResponse,
  useGetNotificationChannels,
  useGetNotificationEvents,
  useGetNotificationPreferences,
  useManageNotificationPreference,
} from 'src/graphql/entities/notification-preference';
import { NotificationChannelSelect } from './components/notification-channel-select';
import { NotificationRoleSelect } from './components/notification-role-select';
import { WeeklyDigestSiteExclusions } from './components/weekly-digest-site-exclusions';
import { StyledTableContainer } from './styled';
import {
  channelsForNotificationEvent,
  isWeeklyDigestEvent,
  rolesPreselectedForEvent,
  weeklyDigestExclusionConfig,
} from './utils';

type NotificationsConfigsTableProps = {
  events: NotificationEventResponse[];
};

export const NotificationsConfigsTable = ({ events }: NotificationsConfigsTableProps) => {
  const theme = useTheme();
  const { notificationChannels } = useGetNotificationChannels();
  const { getNotificationPreferenceBy, notificationPreferences } =
    useGetNotificationPreferences();
  const { manageNotificationPreference } = useManageNotificationPreference();
  const { notificationEventsMap } = useGetNotificationEvents();

  const getSelectedRolesForEvent = useCallback(
    (eventId: string) => {
      const selectedRoleIds = notificationPreferences
        .filter((p) => p.notificationEventId === eventId)
        .flatMap((p) =>
          p.userNotificationRole?.filter((r) => r.isActive).map((r) => r.notificationRole),
        );
      return unique(selectedRoleIds);
    },
    [notificationPreferences],
  );

  const getRoleOptionsForEvent = useCallback(
    (eventId: string) => {
      const event = notificationEventsMap.get(eventId);
      return event?.roles ?? [];
    },
    [notificationEventsMap],
  );

  const getSelectedChannelsForEvent = useCallback(
    (eventId: string) => {
      const selectedChannelIds = notificationPreferences
        .filter((p) => p.notificationEventId === eventId && p.isActive)
        .map((p) => p.notificationChannelId);
      return unique(selectedChannelIds);
    },
    [notificationPreferences],
  );

  const handleChannelChange = useCallback(
    (eventId: string, channelId: string) => async (_: ChangeEvent, checked: boolean) => {
      const currentNotificationPreference = getNotificationPreferenceBy(eventId, channelId);
      const operation = !currentNotificationPreference?.notificationPreferenceId
        ? 'create'
        : 'update';

      const nextValues: NotificationPreferenceInputData = {
        operation,
        notificationPreferenceId:
          currentNotificationPreference?.notificationPreferenceId ?? crypto.randomUUID(),
        isActive: checked,
        notificationChannelId: channelId,
        notificationEventId: eventId,
      };

      const selectedRoles = getSelectedRolesForEvent(eventId);

      if (operation === 'create') {
        nextValues.userNotificationRole = selectedRoles.map((role) => ({
          isActive: true,
          notificationRoleId: role.notificationRoleId,
          userNotificationRoleId: crypto.randomUUID(),
          operation: 'create',
        }));
      }

      const results: NotificationPreferenceInputData[] = [];

      // Handle case when user selects channel but no roles were selected yet.
      // In this case, we automatically select the event's default roles, or all of them.
      if (
        !selectedRoles.length &&
        !currentNotificationPreference?.userNotificationRole.length
      ) {
        const event: NotificationEventResponse | undefined =
          notificationEventsMap.get(eventId);
        const preselectedRoles: NotificationEventResponse['roles'] = event
          ? rolesPreselectedForEvent(event)
          : [];
        nextValues.userNotificationRole = preselectedRoles.map((role) => ({
          isActive: true,
          notificationRoleId: role.notificationRoleId,
          userNotificationRoleId: crypto.randomUUID(),
          operation: 'create',
        }));

        const otherChannels = differenceWith(
          channelsForNotificationEvent(eventId, notificationChannels).filter(
            (c) => c.id !== channelId,
          ),
          notificationPreferences.filter(
            (p) => p.notificationEventId === eventId || p.isActive,
          ),
          (a, b) => a.id === b.notificationChannelId,
        ).map((otherChannel) => {
          return {
            ...nextValues,
            notificationPreferenceId: crypto.randomUUID(),
            notificationChannelId: otherChannel.id,
            isActive: false,
            userNotificationRole: nextValues.userNotificationRole?.map((r) => ({
              ...r,
              userNotificationRoleId: crypto.randomUUID(),
            })),
          };
        });
        results.push(...otherChannels);
      }
      results.push(nextValues);
      await manageNotificationPreference(results);
    },
    [
      getNotificationPreferenceBy,
      getSelectedRolesForEvent,
      manageNotificationPreference,
      notificationChannels,
      notificationEventsMap,
      notificationPreferences,
    ],
  );

  const handleRolesChange =
    (eventId: string) => async (nextValues: NotificationRoleResponse[]) => {
      const prev = getSelectedRolesForEvent(eventId);

      const selectedRoleIds = differenceWith(nextValues, prev, (a, b) => a.id === b.id).map<
        [string, boolean]
      >((value) => [value.id, true]);
      const deselectedRoleIds = differenceWith(prev, nextValues, (a, b) => a.id === b.id).map<
        [string, boolean]
      >((value) => [value.id, false]);

      const roles: { notificationRoleId: string; isActive: boolean }[] = selectedRoleIds
        .concat(deselectedRoleIds)
        .map(([id, isActive]) => ({
          isActive,
          notificationRoleId: id,
        }));

      const existingPreferences = notificationPreferences
        .filter((p) => p.notificationEventId === eventId)
        .map<NotificationPreferenceInputData>((p) => {
          const roleIdToUserNotificationRoleIdMap = new Map(
            p.userNotificationRole.map((r) => [
              r.notificationRoleId,
              r.userNotificationRoleId,
            ]),
          );
          return {
            operation: 'update',
            isActive: p.isActive,
            notificationPreferenceId: p.notificationPreferenceId,
            notificationChannelId: p.notificationChannelId,
            notificationEventId: p.notificationEventId,
            userNotificationRole: roles.map((role) => ({
              ...role,
              operation: roleIdToUserNotificationRoleIdMap.has(role.notificationRoleId)
                ? 'update'
                : 'create',
              userNotificationRoleId:
                roleIdToUserNotificationRoleIdMap.get(role.notificationRoleId) ??
                crypto.randomUUID(),
            })),
          };
        });

      const newValues = differenceWith(
        channelsForNotificationEvent(eventId, notificationChannels),
        existingPreferences,
        (a, b) => a.id === b.notificationChannelId,
      ).map<NotificationPreferenceInputData>((channel) => ({
        operation: 'create',
        notificationPreferenceId: crypto.randomUUID(),
        isActive: false,
        notificationChannelId: channel.id,
        notificationEventId: eventId,
        userNotificationRole: roles.map((r) => ({
          ...r,
          operation: 'create',
          userNotificationRoleId: crypto.randomUUID(),
        })),
      }));

      await manageNotificationPreference(existingPreferences.concat(newValues));
    };

  const handleExcludedSitesChange = (eventId: string) => async (addressIds: string[]) => {
    const preference = getNotificationPreferenceBy(eventId, NotificationChannelType.Email);
    if (!preference) return;

    const saved = await manageNotificationPreference([
      {
        operation: 'update',
        notificationPreferenceId: preference.notificationPreferenceId,
        isActive: preference.isActive,
        notificationChannelId: preference.notificationChannelId,
        notificationEventId: preference.notificationEventId,
        config: weeklyDigestExclusionConfig(addressIds),
      },
    ]);
    if (!saved.length) {
      throw new Error('Weekly site digest exclusions were not saved');
    }
  };

  return notificationChannels.length > 0 ? (
    <StyledTableContainer>
      <Table size="small">
        <TableHead>
          <TableRow
            sx={{
              height: theme.spacing(6),
              backgroundColor: theme.palette.custom.OV.sys.surfaceBright,
            }}
          >
            <TableCell>Event</TableCell>
            <TableCell>When</TableCell>
            <TableCell>Via</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((event) => {
            const selectedRoles = getSelectedRolesForEvent(event.id);
            const roleOptions = getRoleOptionsForEvent(event.id);
            const selectedChannelIds = getSelectedChannelsForEvent(event.id);
            const eventChannels = channelsForNotificationEvent(event.id, notificationChannels);
            return (
              <TableRow key={event.id} hover>
                <TableCell
                  component="th"
                  scope="row"
                  size="small"
                  key={event.id}
                  sx={{ width: '30%' }}
                >
                  <Typography variant="body2">{event.name}</Typography>
                </TableCell>
                <TableCell>
                  {isWeeklyDigestEvent(event.id) ? (
                    <WeeklyDigestSiteExclusions
                      emailEnabled={selectedChannelIds.includes(NotificationChannelType.Email)}
                      partnerId={
                        getNotificationPreferenceBy(event.id, NotificationChannelType.Email)
                          ?.partnerId
                      }
                      config={
                        getNotificationPreferenceBy(event.id, NotificationChannelType.Email)
                          ?.config
                      }
                      onChange={handleExcludedSitesChange(event.id)}
                    />
                  ) : (
                    <V2ThemeProvider>
                      <NotificationRoleSelect
                        roleOptions={roleOptions}
                        selectedValues={selectedRoles}
                        onChange={handleRolesChange(event.id)}
                      />
                    </V2ThemeProvider>
                  )}
                </TableCell>
                <TableCell sx={{ width: '20%' }}>
                  <FormGroup>
                    {eventChannels.map((channel) => (
                      <NotificationChannelSelect
                        key={channel.id}
                        channel={channel}
                        checked={selectedChannelIds.some((id) => id === channel.id)}
                        onChange={handleChannelChange(event.id, channel.id)}
                      />
                    ))}
                  </FormGroup>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </StyledTableContainer>
  ) : (
    <Typography variant="body1" align="center">
      No active notification channels.
    </Typography>
  );
};
